// Video spend quote for PlatformFormatPicker.
//
// Meta-only and PMax-only per-product counts are unconditional — the client
// can state them without a campaign, products, or templates. Mixed Meta+PMax
// (Meta video + PMax 9:16 in the same selection) is the only shape that
// depends on the five backend conjuncts, so it is the only shape that fetches
// POST /api/ads/preview.
//
// MONEY: billable.videoMasters is a RUN TOTAL (products × masters/product).
// Preview with zero productIds returns videoMasters=0 even when the
// selection would bill three Omni masters — never fetch in that shape,
// and never display a number whose requestKey is not the current key.

import { useEffect, useMemo, useState } from 'react';
import { apiJson } from '../../auth/apiFetch';

export const VIDEO_QUOTE_DEBOUNCE_MS = 300;

export type VideoQuoteContext = {
  campaignId: string | null;
  productIds: string[];
  templateIds: string[];
  videoDurationSec: number;
};

export type VideoSpendQuote =
  | { status: 'idle' }
  | { status: 'blocked'; reason: 'no-campaign' | 'no-products' | 'no-templates'; minPerProduct: number; maxPerProduct: number }
  | { status: 'pending'; minPerProduct: number; maxPerProduct: number }
  | { status: 'error'; minPerProduct: number; maxPerProduct: number }
  | {
      status: 'ready';
      perProduct: number | null; // null → total does not divide evenly; show total
      totalMasters: number;
      productCount: number;
    };

type Remote =
  | { key: string; status: 'error' }
  | {
      key: string;
      status: 'ready';
      perProduct: number | null;
      totalMasters: number;
      productCount: number;
    };

function quoteKey(
  ctx: VideoQuoteContext,
  staticKeys: string[],
  videoKeys: string[]
): string {
  return [
    ctx.campaignId || '',
    ctx.productIds.join(','),
    ctx.templateIds.join(','),
    String(ctx.videoDurationSec),
    staticKeys.join(','),
    videoKeys.join(',')
  ].join('|');
}

function derivedKinds(staticKeys: string[], videoKeys: string[]): 'image' | 'video' | 'both' {
  if (staticKeys.length && videoKeys.length) return 'both';
  if (videoKeys.length) return 'video';
  return 'image';
}

function isAbort(err: unknown): boolean {
  return (
    (typeof DOMException !== 'undefined' && err instanceof DOMException && err.name === 'AbortError')
    || (err instanceof Error && err.name === 'AbortError')
  );
}

function mixedBounds(unsharedPerProduct: number): { minPerProduct: number; maxPerProduct: number } {
  return {
    minPerProduct: Math.max(1, unsharedPerProduct - 1),
    maxPerProduct: unsharedPerProduct
  };
}

export function useVideoSpendQuote(
  ctx: VideoQuoteContext | undefined,
  staticKeys: string[],
  videoKeys: string[],
  request: { sharingPossible: boolean; unsharedPerProduct: number }
): VideoSpendQuote {
  const hasVideo = videoKeys.length > 0;
  const sharingPossible = request.sharingPossible;
  const unsharedPerProduct = request.unsharedPerProduct;
  const campaignId = ctx?.campaignId || null;
  const productIds = ctx?.productIds || [];
  const templateIds = ctx?.templateIds || [];
  const videoDurationSec = ctx?.videoDurationSec ?? 10;
  const needsQuote = hasVideo && sharingPossible;

  // NOTE (fixed while applying this draft): `VideoSpendQuote extends
  // {status:'blocked'; reason: infer R} ? R | null : never` does not
  // distribute over VideoSpendQuote's members the way it would over a bare
  // generic type parameter — VideoSpendQuote here is a concrete union, so
  // the whole union is tested against the pattern at once, fails, and the
  // conditional collapses to `never` (tsc: "Type '\"no-campaign\" | ... |
  // null' is not assignable to type 'never'"). Spelling the literal union
  // out directly is equivalent and actually compiles.
  const blockedReason: 'no-campaign' | 'no-products' | 'no-templates' | null =
    !needsQuote ? null
    : !campaignId ? 'no-campaign'
    : productIds.length === 0 ? 'no-products'
    : templateIds.length === 0 ? 'no-templates'
    : null;

  const key = useMemo(() => {
    if (!needsQuote || !ctx || blockedReason) return '';
    return quoteKey(
      { campaignId, productIds, templateIds, videoDurationSec },
      staticKeys,
      videoKeys
    );
  }, [needsQuote, campaignId, productIds, templateIds, videoDurationSec, staticKeys, videoKeys, blockedReason, ctx]);

  const [remote, setRemote] = useState<Remote | null>(null);

  useEffect(() => {
    if (!key) {
      setRemote(null);
      return;
    }
    const ac = new AbortController();
    const t = window.setTimeout(() => {
      const kinds = derivedKinds(staticKeys, videoKeys);
      const platformFormat = staticKeys[0] || videoKeys[0] || 'meta_feed_1_1';
      apiJson<{
        productCount?: unknown;
        billable?: { videoMasters?: unknown };
      }>('/api/ads/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: ac.signal,
        body: JSON.stringify({
          campaignId,
          productIds,
          mediaIds: [],
          templateIds,
          cta: { text: '', url: '' },
          urlParams: '',
          platformFormat,
          kinds,
          preset: 'explicit',
          staticFormats: staticKeys,
          videoFormats: videoKeys,
          expandStaticFormats: false,
          videoDurationSec
        })
      })
        .then(res => {
          const raw = res?.billable?.videoMasters;
          // apiJson is an unchecked cast — refuse anything that is not a
          // real non-negative number rather than rendering `undefined`.
          if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) {
            setRemote({ key, status: 'error' });
            return;
          }
          const n = productIds.length;
          setRemote({
            key,
            status: 'ready',
            totalMasters: raw,
            productCount: n,
            perProduct: n > 0 && raw % n === 0 ? raw / n : null
          });
        })
        .catch(err => {
          if (isAbort(err)) return;
          setRemote({ key, status: 'error' });
        });
    }, VIDEO_QUOTE_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(t);
      ac.abort();
    };
  }, [key, campaignId, productIds, templateIds, videoDurationSec, staticKeys, videoKeys]);

  if (!hasVideo) return { status: 'idle' };
  if (!needsQuote) {
    return {
      status: 'ready',
      perProduct: unsharedPerProduct,
      totalMasters: unsharedPerProduct,
      productCount: 1
    };
  }
  const bounds = mixedBounds(unsharedPerProduct);
  if (blockedReason) return { status: 'blocked', reason: blockedReason, ...bounds };
  if (remote && remote.key === key) {
    if (remote.status === 'error') return { status: 'error', ...bounds };
    return {
      status: 'ready',
      perProduct: remote.perProduct,
      totalMasters: remote.totalMasters,
      productCount: remote.productCount
    };
  }
  // Key changed, or first tick: drop any previous number.
  return { status: 'pending', ...bounds };
}
